import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { defectTypeNietInzetbaar } from '@/domain/defecten/defect';
import { groupDefectsByEenheid } from '@/domain/defecten/defectEenheid';
import { filterPlankDefecten } from '@/domain/defecten/defectFilter';
import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';
import { LocatieVoorEenheden } from '@/types/LocatieVoorEenheden';
import { bepaalUitloopLocatie } from '@/utils/helpers';
import { getReserveByStel } from '@/views/ddp/utils/reserve';

import notitieStateCreator from '../state/notitieStateCreator';
import plankSelectionStateCreator from '../state/plankSelectionStateCreator';
import plankTableStateCreator from '../state/plankTableStateCreator';
import plankTooltipStateCreator from '../state/plankTooltipStateCreator';
import sidepanelStateCreator from '../state/sidePanelStateCreator';
import { PlankStoreV2 } from '../state/types';
import { PlankDataStore } from '../state/types/PlankDataStore';
import voorraadbeheerStateCreator from '../state/voorraadbeheerStateCreator';
import { Reserve } from '../types';
import { EenheidStatus } from '../types/ddpTypes';
import {
  ddpConflictAcceptedLastComparator,
  ddpConflictBijzonderheidscodeComparator,
  ddpConflictTimeComparator,
  ddpConflictVerkeerdMatComparator,
  geefConflictMaterieelSoort,
  geefInzetVoorConflict,
  getConflictenByStel,
  vindEersteNietGeaccepteerdInzet,
} from '../utils/conflict';
import { ddpColumns } from '../utils/ddp';
import { mapDienstregelpuntenToRbc } from '../utils/voorraadbeheer';

function reducePerDefect(defect: Defect, bySoort) {
  const key = `${defect.materieelSoort}`;
  bySoort[key] = bySoort?.[key] || [];
  bySoort[key].push(defect);
  return bySoort;
}

function reducePerConflict(conflict: Conflict, bySoort) {
  const key = `${geefConflictMaterieelSoort(conflict)}`;
  bySoort[key] = bySoort?.[key] || [];
  bySoort[key].push(conflict);
  return bySoort;
}

/**
 * Unieke sleutel voor conflict om te bepalen of er een nieuwe inschrijven in de plank worden benodigd
 */
const getUniekSleutel = (conflict: Conflict) =>
  `${conflict.eenheidNummer}-${conflict.werklijnId}-${conflict.elementen[0].werklijnElementId}`;

/**
 * Verkeerd Mat en Versterkt conflicten worden beide onder dezelfde 'VERKEERD MAT' kolom in de plank getoond
 * Deze methode verzekert dat als de getoonde informatie precies hetzelfde is, het niet dubbel wordt weergegeven.
 */
function findHetzelfdeVerkeerdmatInschrijven(
  bySoort: object,
  key: string,
  conflict: Conflict,
  uniekSleutelConflict: string,
) {
  let isAlreadyAdded = false;
  bySoort[key]
    .filter((conflictPerMat: Conflict) => conflictPerMat.eenheidNummer === conflict.eenheidNummer)
    .find((conflictPerEenheid: Conflict) => {
      const existingKey = getUniekSleutel(conflictPerEenheid);
      if (existingKey === uniekSleutelConflict) {
        isAlreadyAdded = true;
        return true;
      }
      return false;
    });
  return isAlreadyAdded;
}

const groupDefectenByKnoop = (
  defecten: Defect[],
  locatieVoorEenheden: LocatieVoorEenheden[],
  dienstregelpunten: { [dienstregelpun: string]: [string, string, string] },
): Map<string, Defect[]> => {
  const defectenPerKnoop = new Map<string, Defect[]>();
  Map.groupBy(
    defecten,
    (defect) => bepaalUitloopLocatie(defect.eenheidNummer, locatieVoorEenheden)?.dienstregelpunt ?? 'Onbekend',
  ).forEach((defectenOpKnoop, dienstregelpunt) => {
    const knoop = dienstregelpunten?.[dienstregelpunt]?.[2] ?? 'Onbekend';
    if (defectenPerKnoop.has(knoop))
      defectenPerKnoop.set(knoop, [...(defectenPerKnoop.get(knoop) ?? []), ...defectenOpKnoop]);
    else defectenPerKnoop.set(knoop, defectenOpKnoop);
  });
  return defectenPerKnoop;
};
const groupConflictenByKnoop = (
  conflicten: Conflict[],
  dienstregelpunten: { [dienstregelpun: string]: [string, string, string] },
  isVerkeerdMat: boolean = false,
): Map<string, Conflict[]> => {
  const conflictenPerKnoop = new Map<string, Conflict[]>();
  Map.groupBy(
    conflicten,
    (conflict) =>
      (conflict.type === 'MANCO'
        ? geefInzetVoorConflict(conflict)
        : (vindEersteNietGeaccepteerdInzet(conflict) ?? conflict?.elementen?.[0])
      )?.dienstregelpunt ?? 'Onbekend',
  ).forEach((conflicten, dienstregelpunt) => {
    conflicten.sort(ddpConflictTimeComparator);
    if (isVerkeerdMat) conflicten.sort(ddpConflictVerkeerdMatComparator);
    conflicten.sort(ddpConflictBijzonderheidscodeComparator);
    const knoop = dienstregelpunten?.[dienstregelpunt]?.[2] ?? 'Onbekend';
    if (conflictenPerKnoop.has(knoop))
      conflictenPerKnoop.set(knoop, [...(conflictenPerKnoop.get(knoop) ?? []), ...conflicten]);
    else conflictenPerKnoop.set(knoop, conflicten);
  });
  return conflictenPerKnoop;
};

/**
 * Zustand store hook to show and customize DDP table data visibility.
 */
const usePlankTableStore = create<PlankDataStore & PlankStoreV2>()(
  persist(
    (set, get, _store) => ({
      ...plankTooltipStateCreator(set, get, _store),
      ...voorraadbeheerStateCreator(set, get, _store),
      ...plankTableStateCreator(set, get, _store),
      ...sidepanelStateCreator(set, get, _store),
      ...notitieStateCreator(set, get, _store),
      ...plankSelectionStateCreator(set, get, _store),
      eenheidStatus: {},
      defectNietInzetbaarVanaf: {},
      mancosPerMaterieel: {},
      verkeerdMatPerMaterieel: {},
      reservesPerMaterieel: {},
      defectenInzetbaarPerMaterieel: {},
      defectenBeperkInzetbaarPerMaterieel: {},
      defectenNietInzetbaarPerMaterieel: {},
      defectenInOnderhoudPerMaterieel: {},
      locatieVoorEenheden: [] as LocatieVoorEenheden[],
      defectenByStel: {},
      conflictenByStel: {},
      reserveByStel: {},
      reservesPerKnoop: new Map<string, Reserve[]>(),
      mancosPerKnoop: new Map<string, Conflict[]>(),
      verkeerdMatPerKnoop: new Map<string, Conflict[]>(),
      defectenInzetbaarPerKnoop: new Map<string, Defect[]>(),
      defectenBeperktInzetbaarPerKnoop: new Map<string, Defect[]>(),
      defectenNietInzetbaarPerKnoop: new Map<string, Defect[]>(),
      defectenInOnderhoudPerKnoop: new Map<string, Defect[]>(),

      setDefecten: async (defecten: Defect[], locatieVoorEenheden: LocatieVoorEenheden[]) => {
        const { getDefectNotitie, eenheidStatus } = get();
        // Filter de relevante defecten
        defecten = filterPlankDefecten(defecten, eenheidStatus);
        /** Op eenheid samengevoegde defecten. */
        const eenheidDefecten = groupDefectsByEenheid(defecten);
        const locaties = Object.fromEntries(locatieVoorEenheden.map((loc) => [loc.eenheidNummer, loc]));

        /** Lijst van meest relevante defecten uniek per eenheidNummer. Voorzien van de huidigeLocatie. */
        const _defects = eenheidDefecten.map((defEntry) => {
          const topDefect = defEntry[1][0];
          topDefect.huidigeLocatie = locaties?.[topDefect.eenheidNummer];
          return { ...topDefect };
        });

        const allByStel = Object.fromEntries(eenheidDefecten);
        const defectenInzetbaarPerMaterieel = _defects
          .filter((defect) => ['GEEN_BEPERKINGEN'].includes(defect.soort) && !defectTypeNietInzetbaar(defect))
          .reduce<{ [matTyp: string]: Defect[] }>((bySoort, defect) => {
            return reducePerDefect(defect, bySoort);
          }, {});
        const defectenBeperkInzetbaarPerMaterieel = _defects
          .filter((defect) => ['BEPERKING_IN_TREINDIENST'].includes(defect.soort) && !defectTypeNietInzetbaar(defect))
          .reduce<{ [matTyp: string]: Defect[] }>((bySoort, defect) => {
            return reducePerDefect(defect, bySoort);
          }, {});
        const defectenNietInzetbaarPerMaterieel = _defects.reduce<{ [matType: string]: Defect[] }>((result, defect) => {
          const notitie = getDefectNotitie(defect);
          if (defectTypeNietInzetbaar(defect) && notitie?.status !== 'NOTITIE_OP_SB_OB') {
            return reducePerDefect(defect, result);
          }
          return result;
        }, {});
        const defectenInOnderhoudPerMaterieel = _defects.reduce<{ [matType: string]: Defect[] }>((result, defect) => {
          const notitie = getDefectNotitie(defect);
          if (defectTypeNietInzetbaar(defect) && notitie?.status === 'NOTITIE_OP_SB_OB') {
            return reducePerDefect(defect, result);
          }
          return result;
        }, {});
        const defectNietInzetbaarVanaf = Object.fromEntries(
          Object.entries(allByStel)
            .map((stelDefsEntry) => {
              const reducedDefect = stelDefsEntry[1].reduce((dateTime: string | null, def: Defect) => {
                const nmiTime = new Date(String(def.nietMeerInzetbaarVanaf)).getTime();
                if (def.nietMeerInzetbaarVanaf === null || nmiTime <= new Date().getTime()) return dateTime;
                if (dateTime === null) return def.nietMeerInzetbaarVanaf;
                return new Date(dateTime).getTime() <= nmiTime ? dateTime : def.nietMeerInzetbaarVanaf;
              }, null);
              return [stelDefsEntry[0], reducedDefect];
            })
            .filter((stelDefsEntry) => stelDefsEntry[1] !== null),
        );
        // Map defecten naar knoop
        const dienstregelpunten = mapDienstregelpuntenToRbc();
        const defectenInzetbaarPerKnoop = groupDefectenByKnoop(
          Object.values(defectenInzetbaarPerMaterieel).flat(),
          locatieVoorEenheden,
          dienstregelpunten,
        );
        const defectenBeperktInzetbaarPerKnoop = groupDefectenByKnoop(
          Object.values(defectenBeperkInzetbaarPerMaterieel).flat(),
          locatieVoorEenheden,
          dienstregelpunten,
        );
        const defectenNietInzetbaarPerKnoop = groupDefectenByKnoop(
          Object.values(defectenNietInzetbaarPerMaterieel).flat(),
          locatieVoorEenheden,
          dienstregelpunten,
        );
        const defectenInOnderhoudPerKnoop = groupDefectenByKnoop(
          Object.values(defectenInOnderhoudPerMaterieel).flat(),
          locatieVoorEenheden,
          dienstregelpunten,
        );

        set({
          defectNietInzetbaarVanaf,
          defectenInzetbaarPerMaterieel,
          defectenBeperkInzetbaarPerMaterieel,
          defectenNietInzetbaarPerMaterieel,
          defectenInOnderhoudPerMaterieel,
          defectenByStel: { ...allByStel },
          defectenInzetbaarPerKnoop,
          defectenBeperktInzetbaarPerKnoop,
          defectenNietInzetbaarPerKnoop,
          defectenInOnderhoudPerKnoop,
        });
      },
      setConflicten: async (conflicten: Conflict[]) => {
        const { eenheidStatus } = get();
        const relevantConflicts = conflicten.filter((conflict) =>
          ['MANCO', 'VERKEERD_MATERIEEL', 'VERSTERKT_MATERIEEL'].includes(conflict.type),
        );
        const _conflicts =
          Object.keys(eenheidStatus).length > 0
            ? relevantConflicts.filter(
                (conflict) =>
                  conflict.eenheidNummer === null ||
                  eenheidStatus?.[conflict.eenheidNummer]?.toelating === 'OPERATIONEEL',
              )
            : relevantConflicts;
        _conflicts.sort(ddpConflictTimeComparator);

        const conflictenByStel = getConflictenByStel(_conflicts);
        const verkeerdMatPerMaterieel = _conflicts
          .filter((conflict) => ['VERKEERD_MATERIEEL', 'VERSTERKT_MATERIEEL'].includes(conflict.type))
          .reduce((bySoort, conflict) => {
            const mat = geefConflictMaterieelSoort(conflict);
            const key = `${mat}`;
            bySoort[key] = bySoort?.[key] || [];
            const uniekSleutelConflict = getUniekSleutel(conflict);
            const isAlreadyAdded = findHetzelfdeVerkeerdmatInschrijven(bySoort, key, conflict, uniekSleutelConflict);
            if (!isAlreadyAdded) {
              bySoort[key].push(conflict);
            }

            return bySoort;
          }, {});
        const dienstregelpunten = mapDienstregelpuntenToRbc();
        const verkeerdMatPerKnoop = groupConflictenByKnoop(
          Object.values(verkeerdMatPerMaterieel).flat() as Conflict[],
          dienstregelpunten,
          true,
        );

        const mancosPerMaterieel = _conflicts
          .filter((conflict) => ['MANCO'].includes(conflict.type))
          // allow if at least one element has no claim OR has claimSoort === 'EBKSB', or if fully accepted
          .filter(
            (conflict) =>
              conflict.geaccepteerdConflict ||
              conflict.elementen.some(
                (conflictElement) =>
                  !conflictElement.claimSoort || ['EBKSB', 'ONB'].includes(conflictElement.claimSoort),
              ),
          )
          .reduce((bySoort, conflict) => {
            return reducePerConflict(conflict, bySoort);
          }, {});

        const mancosPerKnoop = groupConflictenByKnoop(
          Object.values(mancosPerMaterieel).flat() as Conflict[],
          dienstregelpunten,
        );

        for (const key of Object.keys(verkeerdMatPerMaterieel)) {
          // Verkeerd Mat en Versterkt conflicten worden in een lijst gezet en moet opnieuw worden gesorteer volgens:
          // 'Ernst' van verkeerd mat
          // Versterkt conflicten
          // Tijd
          // Volledig geaccepteerde conflicten worden onderaan gezet
          verkeerdMatPerMaterieel[key].sort(ddpConflictVerkeerdMatComparator);
          verkeerdMatPerMaterieel[key].sort(ddpConflictBijzonderheidscodeComparator);
          verkeerdMatPerMaterieel[key].sort(ddpConflictAcceptedLastComparator);
        }

        for (const key of Object.keys(mancosPerMaterieel)) {
          // Volledig geaccepteerde manco conflicten worden onderaan gezet
          mancosPerMaterieel[key].sort(ddpConflictBijzonderheidscodeComparator);
          mancosPerMaterieel[key].sort(ddpConflictAcceptedLastComparator);
        }
        set(() => {
          return {
            verkeerdMatPerMaterieel,
            mancosPerMaterieel,
            conflictenByStel,
            verkeerdMatPerKnoop,
            mancosPerKnoop,
          };
        });
      },
      setReserves: async (reserves: Reserve[]) => {
        const { eenheidStatus } = get();
        const dienstregelpunten = mapDienstregelpuntenToRbc();
        // Filter uit reserves die alleen een operationele eenheid heeft
        const operationeleReserves =
          Object.keys(eenheidStatus).length > 0
            ? reserves.filter((reserve) => eenheidStatus?.[reserve.eenheid]?.toelating === 'OPERATIONEEL')
            : reserves;

        const reserveByStel = getReserveByStel(operationeleReserves);
        const reservesPerMaterieel = operationeleReserves.reduce((bySoort, reserve) => {
          const mat = reserve.materieelType;
          const key = `${mat}`;
          bySoort[key] = bySoort?.[key] || [];
          bySoort[key].push(reserve);
          return bySoort;
        }, {});

        const reservesPerKnoop = new Map<string, Reserve[]>();
        Map.groupBy(operationeleReserves, ({ dienstregelpunt }) => dienstregelpunt).forEach(
          (value, dienstregelpunt) => {
            const knoop = dienstregelpunten?.[dienstregelpunt]?.[2] ?? 'Onbekend';
            if (reservesPerKnoop.has(knoop))
              reservesPerKnoop.set(knoop, [...(reservesPerKnoop.get(knoop) ?? []), ...value]);
            else reservesPerKnoop.set(knoop, value);
          },
        );

        set({
          reservesPerMaterieel,
          reserveByStel,
          reservesPerKnoop,
        });
      },
      async setLocatieVoorEenheden(locatieVoorEenheden: LocatieVoorEenheden[]) {
        set({
          locatieVoorEenheden,
        });
      },
      setEenhedenStatus(eenheidStatus: EenheidStatus[]) {
        // Voeg eenheid status toe aan alle Defecten.
        // Voeg eenheid status toe aan alle Conflicten.
        // Voeg eenheid status toe aan alle Reserves.
        set({
          eenheidStatus: Object.fromEntries(eenheidStatus.map((status) => [status.nummer, status])),
        });
      },

      getRelatedDefect: (stel: string) => {
        return get().defectenByStel?.[stel]?.[0];
      },
      moveDefectToOnderhoud: (defect: Defect) => {
        if (!defectTypeNietInzetbaar(defect)) return;
        set((state) => {
          const matType = defect.materieelSoort;
          return {
            ...state,
            defectenInOnderhoudPerMaterieel: {
              ...state.defectenInOnderhoudPerMaterieel,
              [`${matType}`]: [...(state.defectenInOnderhoudPerMaterieel?.[`${matType}`] ?? []), defect],
            },
            defectenNietInzetbaarPerMaterieel: {
              ...state.defectenNietInzetbaarPerMaterieel,
              [`${matType}`]: [
                ...state.defectenNietInzetbaarPerMaterieel[`${matType}`].filter(
                  (def) => def.werkorderId !== defect.werkorderId && def.id !== defect.id,
                ),
              ],
            },
          };
        });
      },
      moveDefectFromOnderhoud: (defect: Defect) => {
        if (!defectTypeNietInzetbaar(defect)) return;
        set((state) => {
          const matType = defect.materieelSoort;
          return {
            ...state,
            defectenNietInzetbaarPerMaterieel: {
              ...state.defectenNietInzetbaarPerMaterieel,
              [`${matType}`]: [...state.defectenNietInzetbaarPerMaterieel[`${matType}`], defect],
            },
            defectenInOnderhoudPerMaterieel: {
              ...state.defectenInOnderhoudPerMaterieel,
              [`${matType}`]: [
                ...state.defectenInOnderhoudPerMaterieel[`${matType}`].filter(
                  (def) => def.werkorderId !== defect.werkorderId && def.id !== defect.id,
                ),
              ],
            },
          };
        });
      },
      reset: () =>
        set((state) => ({
          ...state,
          table: { ...state.table, rows: [], columns: [...ddpColumns], collapsed: [] },
          tableRegionaal: { ...state.tableRegionaal, rows: [], columns: [...ddpColumns], collapsed: [] },
        })),
    }),
    {
      name: 'plankIndelenSettings', // Persistance key.
      storage: createJSONStorage(() => sessionStorage), // localStorage does not work.
      partialize: (state) => ({ table: state.table, TableRegionaal: state.tableRegionaal }), // Persisteer alleen de tabel gegevens.
    },
  ),
);

export default usePlankTableStore;
